import { Component } from 'react';
import { Link } from 'react-router-dom';
import { Interpolate } from 'react-i18next';
import { withTheme } from 'styled-components';
import moment from 'moment';

// Constants & Services
import { dateWithTimezone } from 'services/date';
import { PAYMENT_STATUS_OPEN, PAYMENT_STATUS_PAID } from 'services/constants';
import { currency } from 'services/currency';

// Components
import CertificateModal from '../../components/CertificateModal/index';
import CertificateIcon from 'src/views/practitioner/components/CertificateIcon';
import ModalDeleteInvoice from './ModalDeleteInvoice';

// Styles
import { Flex, Box } from '@rebass/grid';
import * as styles from './styles';

const prepareInvoiceStatus = (status, i18n) => {
  if (status) {
    if (status.match(/pending/i)) {
      return i18n.t('pending');
    } else if (status.match(/paid/i)) {
      return i18n.t('paid_invoice');
    } else {
      return i18n.t('invoice_status_unpaid');
    }
  }
  return 'Unknown status';
};


class InvoiceInfo extends Component {
  constructor() {
    super();
    this.state = {
      isOpenCertModal: false,
      modalIsOpen: false,
      paymentModalIsOpen: false
    };
    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.deleteInvoice = this.deleteInvoice.bind(this);
    this.triggerCertModal = this.triggerCertModal.bind(this);
  }

  openModal() {
    this.setState({ modalIsOpen: true });
  }

  closeModal() {
    this.setState({ modalIsOpen: false });
  }

  triggerCertModal() {
    const { isOpenCertModal } = this.state;
    this.setState({ isOpenCertModal: !isOpenCertModal });
  }

  deleteInvoice() {
    this.props.deleteInvoice();
    this.closeModal();
  }

  render() {
    const { invoice, i18n, appointmentId, openPaymentModal, theme } = this.props;
    const { isOpenCertModal } = this.state;

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 'inherit'
        }}
      >
        <Flex style={{ flex: 1, flexBasis: 'inherit', minHeight: 'inherit' }}>
          {/* left section  */}

          <Box width={1 / 2} style={{ borderRight: `1px solid ${theme.color.border}` }}>
            <styles.BodyBlock withTotal>
              <styles.TitleBlock>
                <styles.Title float="left">{i18n.t('invoiced_services')}</styles.Title>
                <styles.Title float="right">{i18n.t('price')}</styles.Title>
              </styles.TitleBlock>
              <styles.PaymentList>
                {invoice.invoiceItems &&
                  invoice.invoiceItems.map(item => (
                    <styles.PaymentItem key={item.serviceId || `${item.name}-${item.price}`}>
                      <styles.PaymentBlock>
                        <styles.SpanLeft>{item.name}</styles.SpanLeft>
                        <styles.SpanRight>{currency(item.price).format()}</styles.SpanRight>
                      </styles.PaymentBlock>
                      <styles.NamenclatureNumber>{item.nomenclature}</styles.NamenclatureNumber>
                    </styles.PaymentItem>
                  ))}
              </styles.PaymentList>
            </styles.BodyBlock>
            <styles.TotalBlock>
              <styles.TotalTitle>{i18n.t('total')}</styles.TotalTitle>
              <styles.TotalDots />
              <styles.TotalValue>{currency(invoice.amount || 0).format()}</styles.TotalValue>
            </styles.TotalBlock>
          </Box>

          {/* right section */}

          <Box width={1 / 2}>
            <styles.BodyBlock>
              <styles.TitleBlock>
                <styles.Title float="left">{i18n.t('invoice')}</styles.Title>
              </styles.TitleBlock>
              <styles.InvoiceStatus>
                {invoice.status === PAYMENT_STATUS_PAID ? (
                  <styles.InvoicePaidImg />
                ) : (
                  <styles.InvoiceActiveImg />
                )}
                <styles.CommonDiv align="left" size="0.875" mb="2px">
                  {i18n.t('invoice_status')}
                </styles.CommonDiv>
                <styles.CommonDiv align="left">
                  <styles.Status isPaid={invoice.status === PAYMENT_STATUS_PAID}>
                    {prepareInvoiceStatus(invoice.status, i18n)}
                  </styles.Status>&ensp;
                  {invoice.status !== PAYMENT_STATUS_PAID && (
                    <Interpolate
                      i18nKey="due_date"
                      date={moment(invoice.dueDate).format('DD.MM.YYYY')}
                    />
                  )}
                </styles.CommonDiv>
              </styles.InvoiceStatus>

              <styles.InfoBlock>
                <styles.Invoice24Img />
                <styles.InfoDetailsBlock>
                  <styles.LabelText mb="2px">{i18n.t('invoice_created')}</styles.LabelText>
                  <styles.CommonDiv align="left">
                    <Interpolate
                      i18nKey="invoice_created_date"
                      date={dateWithTimezone(invoice.createdDateTime, 'DD.MM.YYYY')}
                      time={dateWithTimezone(invoice.createdDateTime)}
                      name={`${invoice.practitioner.firstName} ${invoice.practitioner.lastName}`}
                    />
                  </styles.CommonDiv>
                </styles.InfoDetailsBlock>
              </styles.InfoBlock>

              {invoice.lastChangedDateTime && (
                <styles.InfoBlock>
                  <styles.ChangeImg />
                  <styles.InfoDetailsBlock>
                    <styles.LabelText mb="2px">{i18n.t('invoice_last_change')}</styles.LabelText>
                    <styles.CommonDiv align="left">
                      <Interpolate
                        i18nKey="invoice_created_date"
                        date={dateWithTimezone(invoice.lastChangedDateTime, 'DD.MM.YYYY')}
                        time={dateWithTimezone(invoice.lastChangedDateTime)}
                        name={`${invoice.changedPractitioner.firstName} ${
                          invoice.changedPractitioner.lastName
                        }`}
                      />
                    </styles.CommonDiv>
                  </styles.InfoDetailsBlock>
                </styles.InfoBlock>
              )}

              {/* Certificate info row */}

              <styles.InfoBlock>
                <CertificateIcon />

                <styles.InfoDetailsBlock>
                  <styles.LabelText mb="2px">{i18n.t('certificate_number')}</styles.LabelText>
                  <styles.CommonDiv align="left">
                    {(invoice && invoice.certificateNumber) || i18n.t('no_data')}
                  </styles.CommonDiv>
                </styles.InfoDetailsBlock>

                <styles.CertificateEditImg onClick={this.triggerCertModal} />
              </styles.InfoBlock>
            </styles.BodyBlock>
          </Box>
        </Flex>

        {/* ──── Buttons ─────────────────────────────────────────────────────────────────────── */}

        {invoice.status !== PAYMENT_STATUS_PAID && (
          <styles.ButtonsBlock>
            {/* left */}
            <div>
              {PAYMENT_STATUS_OPEN === invoice.status && (
                <styles.DeleteInvoice onClick={this.openModal}>
                  {i18n.t('delete_invoice')}
                </styles.DeleteInvoice>
              )}
            </div>

            {/* right */}
            <div>
              {PAYMENT_STATUS_OPEN === invoice.status && (
                <Link
                  to={`/practitioner/appointments/${appointmentId}/create-invoice/${invoice.id}`}
                >
                  <styles.ChangeInvoice>{i18n.t('change_invoice')}</styles.ChangeInvoice>
                </Link>
              )}

              {PAYMENT_STATUS_OPEN === invoice.status && (
                <styles.ConfirmPayment onClick={openPaymentModal}>
                  {i18n.t('confirm_pay_appointment_details')}
                </styles.ConfirmPayment>
              )}
            </div>
          </styles.ButtonsBlock>
        )}

        {/* ──── Modal ──────────────────────────────────────────────────────────────────────── */}

        <ModalDeleteInvoice
          i18n={i18n}
          isOpen={this.state.modalIsOpen}
          closeModal={this.closeModal}
          deleteInvoice={this.deleteInvoice}
        />

        <CertificateModal
          isOpen={isOpenCertModal}
          closeModal={this.triggerCertModal}
          invoice={invoice}
        />
      </div>
    );
  }
}

export default withTheme(InvoiceInfo);